import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import {
  buildBrandCollaborationJourneyItems,
  buildInfluencerCollaborationJourneyItems,
} from '../../utils/collaborations/buildCollaborationJourneyItems';
import { countActiveBrandJourneyItems } from '../../utils/collaborations/brandOperationsUtils';
import { countActiveJourneyItems } from '../../utils/collaborations/collaborationJourneyStats';

const firstCount = (...values) => values.find((value) => value !== undefined && value !== null);

/** Matches brand Collaborations → Active summary card. */
export function countActiveCollaborations(
  applications = [],
  invites = [],
  collaborationStatuses = {},
) {
  const journeyItems = buildBrandCollaborationJourneyItems({
    applications,
    invites,
    collaborationStatuses,
  });
  return countActiveBrandJourneyItems(journeyItems, collaborationStatuses);
}

/** Matches influencer Collaborations → Active summary card. */
export function countActiveInfluencerCollaborations(
  applications = [],
  invites = [],
  acceptanceDetails = {},
) {
  const journeyItems = buildInfluencerCollaborationJourneyItems({
    applications,
    invites,
    acceptanceDetails,
  });
  return countActiveJourneyItems(journeyItems);
}

/** Positive integer badge helper — hide when zero/missing. */
const getPositiveCountBadge = (...values) => {
  const value = firstCount(...values);
  if (value === undefined || value === null) return undefined;
  const n = Number(value) || 0;
  return n > 0 ? n : undefined;
};

/** Sidebar Collaborations badge — Collaborations → Active summary count. */
const getCollaborationBadge = (counts) => getPositiveCountBadge(
  counts.activeCollaborations,
  counts.active,
);

/** Sidebar Campaigns badge — in-market (Active) campaigns only. */
const getCampaignsBadge = (counts) => getPositiveCountBadge(
  counts.activeCampaigns,
  counts.activeCampaignCount,
);

const isInfluencerCollaborationRoute = (pathname) => (
  pathname === '/dashboard/collaborations'
  || pathname.startsWith('/dashboard/applications/')
  || pathname.startsWith('/dashboard/collaboration/')
);

const isBrandCollaborationRoute = (pathname) => (
  pathname === '/dashboard/influencers'
  || pathname.startsWith('/dashboard/applications/')
  || pathname.startsWith('/dashboard/collaboration-status/')
  || pathname.startsWith('/collaborations/')
  || pathname.includes('/marketplace/influencers/')
  || /^\/influencers\/[^/]+(\/campaign\/[^/]+)?$/.test(pathname)
);

export const buildInfluencerSections = ({ counts = {} } = {}) => [
  {
    id: 'primary',
    title: 'Primary',
    collapsible: false,
    items: [
      {
        id: 'overview',
        label: 'Home',
        to: '/dashboard/overview',
        icon: HomeOutlinedIcon,
      },
      {
        id: 'collaborations',
        label: 'Collaborations',
        to: '/dashboard/collaborations',
        icon: AssignmentOutlinedIcon,
        badge: getCollaborationBadge(counts),
        isActive: isInfluencerCollaborationRoute,
      },
    ],
  },
  {
    id: 'insights',
    title: 'Insights',
    collapsible: true,
    defaultOpen: true,
    items: [
      {
        id: 'collaboration-journey',
        label: 'Collaboration Journey',
        to: '/dashboard/collaboration-journey',
        icon: TimelineOutlinedIcon,
      },
      {
        id: 'analytics',
        label: 'Analytics',
        to: '/dashboard/analytics',
        icon: InsightsOutlinedIcon,
        isActive: (pathname) => pathname === '/dashboard/analytics'
          || pathname.startsWith('/dashboard/analytics'),
      },
    ],
  },
  {
    id: 'account',
    title: 'Account',
    collapsible: true,
    defaultOpen: true,
    items: [
      {
        id: 'profile',
        label: 'Profile',
        to: '/dashboard/profile',
        icon: PersonOutlineOutlinedIcon,
      },
    ],
  },
];

export const buildInfluencerCta = () => ({
  label: 'Browse Campaigns',
  to: '/marketplace/campaigns',
});

export const buildBrandSections = ({ counts = {} } = {}) => [
  {
    id: 'primary',
    title: 'Primary',
    collapsible: false,
    items: [
      {
        id: 'overview',
        label: 'Home',
        to: '/dashboard/overview',
        icon: HomeOutlinedIcon,
      },
      {
        id: 'campaigns',
        label: 'Campaigns',
        to: '/dashboard/campaigns',
        icon: CampaignOutlinedIcon,
        badge: getCampaignsBadge(counts),
        isActive: (pathname) => pathname === '/dashboard/campaigns'
          || pathname.startsWith('/dashboard/campaigns/'),
      },
      {
        id: 'collaborations',
        label: 'Collaborations',
        to: '/dashboard/influencers',
        icon: AssignmentOutlinedIcon,
        badge: getCollaborationBadge(counts),
        tourId: 'brand-collaboration-sidebar',
        isActive: isBrandCollaborationRoute,
      },
    ],
  },
  {
    id: 'insights',
    title: 'Insights',
    collapsible: true,
    defaultOpen: true,
    items: [
      {
        id: 'collaboration-journey',
        label: 'Campaign Operations',
        to: '/dashboard/collaboration-journey',
        icon: AccountTreeOutlinedIcon,
      },
      {
        id: 'analytics',
        label: 'Analytics',
        to: '/dashboard/analytics',
        icon: InsightsOutlinedIcon,
        isActive: (pathname) => pathname === '/dashboard/analytics'
          || pathname.startsWith('/dashboard/analytics'),
      },
      {
        id: 'payments',
        label: 'Payments',
        to: '/dashboard/payments',
        icon: PaymentsOutlinedIcon,
      },
    ],
  },
  {
    id: 'account',
    title: 'Account',
    collapsible: true,
    defaultOpen: true,
    items: [
      {
        id: 'profile',
        label: 'Profile',
        to: '/dashboard/profile',
        icon: PersonOutlineOutlinedIcon,
        tourId: 'profile-tab',
      },
    ],
  },
];
